import * as React from 'react';
import * as moment from 'moment';

import { FlyerData, PictureFrameData } from 'data/FlyerData';
import { Popover } from 'react-tiny-popover';
import { ButtonIcon } from 'controller/components/ButtonIcon';
import Menu from '../components/Menu';
import MenuItem from '../components/MenuItem';
import { DEFAULT_VIDEO_DURATION } from '../data/Config';
import { generateClassName } from 'utils';
import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { useUser } from 'hooks/useUser';
import {
  createNewFlyer,
  deleteFlyerNode,
  getFlyers,
  saveFlyer,
  renameFlyer as renameFirebaseFlyer
} from 'utils/FirebaseUtils';
import {
  deleteFileWithUrl,
  duplicateFile,
  getAvailableFilenameWithExtension,
  isTemplateAsset,
  isUrlOnAWS
} from 'utils/S3Utils';
import { promptAnswer } from 'components/PromptView';
import { Spinner } from 'components/Spinner';
import { EmptyState } from './EmptyState';
import { reportBFError } from '../utils/Tracker';

interface ItemProps {
  data: FlyerData;
  onSelect: (flyer: FlyerData) => void;
  onRename: (flyer: FlyerData) => void;
  onDuplicate: (flyer: FlyerData) => void;
  onDelete: (flyer: FlyerData) => void;
}

const ItemView: React.FC<ItemProps> = ({
  data,
  onSelect,
  onRename,
  onDuplicate,
  onDelete
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const didClick = React.useCallback(() => {
    onSelect(data);
  }, [onSelect, data]);

  const onMenuClick = React.useCallback(
    (key: string, e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      switch (key) {
        case 'rename':
          onRename(data);
          break;
        case 'duplicate':
          onDuplicate(data);
          break;
        case 'delete':
          onDelete(data);
          break;
        default:
          break;
      }
      setIsOpen(false);
    },
    [onDelete, onDuplicate, data]
  );

  let layersCount = 0;
  let effectsCount = 0;
  if (data.layers) {
    layersCount = data.layers.length;
    data.layers.forEach(layer => {
      if (layer.effects) {
        effectsCount += Object.keys(layer.effects).length;
      }
    });
  }

  const menu = (
    <Menu onClick={onMenuClick}>
      <MenuItem actionKey="rename">Rename</MenuItem>
      <MenuItem actionKey="duplicate">Duplicate</MenuItem>
      <MenuItem actionKey="delete" className="red">
        Delete
      </MenuItem>
    </Menu>
  );

  return (
    <div className="flyer-item" onClick={didClick}>
      <img
        src={data.thumbnailUrl}
        // crossOrigin="anonymous"
      />
      <div className="flyer-item-text">
        <div className="title">
          {data.name}
          <span className="date"> - {moment(data.date!).fromNow()}</span>
        </div>
        <div className="meta">
          {`Duration: ${Math.round(
            (data.length !== undefined ? data.length : DEFAULT_VIDEO_DURATION) /
              1000
          )}s \u00A0 Layers: ${layersCount} \u00A0 Effects: ${effectsCount}`}
        </div>
      </div>
      <Popover
        isOpen={isOpen}
        positions={['bottom', 'top']}
        align="end"
        reposition={true}
        content={menu}
        onClickOutside={() => {
          setIsOpen(false);
        }}
      >
        <ButtonIcon
          icon="options"
          active={isOpen}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
          }}
        />
      </Popover>
    </div>
  );
};

enum ViewState {
  Loading = 'Loading...',
  Ready = 'Ready',
  Renaming = 'Renaming...',
  Deleting = 'Deleting...',
  Copying = 'Copying...'
}

export const Projects: React.FC = () => {
  const mounted = React.useRef(false);
  const searchTextField = React.useRef<HTMLInputElement>(null);
  const [flyers, setFlyers] = React.useState<FlyerData[]>([]);
  const [viewState, setViewState] = React.useState<ViewState>(
    ViewState.Loading
  );
  const [query, setQuery] = React.useState('');

  const user = useUser();

  const loadFlyers = React.useCallback(() => {
    if (user) {
      // console.log('LOAD FLYERS');
      getFlyers(user.uid).then((items: FlyerData[]) => {
        if (mounted.current) {
          setFlyers(items);
          setViewState(ViewState.Ready);
        }
      });
    }
  }, [user]);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (user) {
      loadFlyers();
    }
  }, [user]);

  const didSelectFlyer = React.useCallback(
    (flyer: FlyerData) => {
      if (viewState !== ViewState.Ready) {
        return;
      }
      storeDispatch({ type: Actions.RESET });
      getStoreState().app.history.push(
        `/flyers/${getStoreState().app.user!.uid}/${flyer.id}`
      );
    },
    [viewState]
  );

  const copyFile = React.useCallback(async (url: string): Promise<string> => {
    if (!isUrlOnAWS(url)) {
      alert('Cannot copy file not in the cloud');
      return url;
    }
    const originalName = url.split('/').pop() as string;
    const extension = originalName.split('.').pop()!;
    const newFileName =
      (await getAvailableFilenameWithExtension(extension)) + '.' + extension;
    return await duplicateFile(originalName, newFileName);
  }, []);

  const renameFlyer = React.useCallback(
    async (flyer: FlyerData) => {
      try {
        if (viewState !== ViewState.Ready) {
          return;
        }
        let newName = await promptAnswer(
          'What should be the new name?',
          flyer.name
        );
        if (!newName) {
          return;
        }
        setViewState(ViewState.Renaming);
        await renameFirebaseFlyer(flyer.id!, newName);
        loadFlyers();
        const { id } = getStoreState().flyer;
        if (id === flyer.id) {
          storeDispatch({
            type: Actions.DID_RENAME_PROJECT,
            id: flyer.id!,
            name: newName
          });
        }
      } catch (error) {
        alert(`Could not duplicate the project because of ${error.message}`);
        reportBFError(
          new Error(
            'Could duplicate save the project because of ' + error.message
          )
        );
        setViewState(ViewState.Ready);
      }
    },
    [viewState]
  );

  const duplicateFlyer = React.useCallback(
    async (flyer: FlyerData) => {
      try {
        if (viewState !== ViewState.Ready) {
          return;
        }
        let newName = await promptAnswer(
          'How do you want to call the new project?'
        );
        if (!newName) {
          return;
        }
        setViewState(ViewState.Copying);
        const uid = getStoreState().app.user!.uid;
        const newFlyerId = await createNewFlyer(newName, uid);
        const thumbnailUrl = await duplicateFile(
          flyer.id + '.jpg',
          newFlyerId + '.jpg'
        );
        const flyerData: FlyerData = JSON.parse(JSON.stringify(flyer));
        const data: FlyerData = {
          ...flyerData,
          name: newName,
          thumbnailUrl
        };
        delete data.id;
        if (data.layers) {
          const layersCount = data.layers.length;
          const copiedUrls = {};
          for (let i = 0; i < layersCount; i++) {
            const layer = data.layers[i];
            const framesCount = layer.frames.length;
            for (let j = 0; j < framesCount; j++) {
              const frame = layer.frames[j] as PictureFrameData;
              if (
                frame.url &&
                isUrlOnAWS(frame.url) &&
                !isTemplateAsset(frame.url)
              ) {
                if (copiedUrls[frame.url!]) {
                  frame.url = copiedUrls[frame.url];
                } else {
                  const newUrl = await copyFile(frame.url);
                  copiedUrls[frame.url] = newUrl;
                  frame.url = newUrl;
                }
              }
            }
          }
        }
        if (
          data.audioUrl &&
          isUrlOnAWS(data.audioUrl) &&
          !isTemplateAsset(data.audioUrl)
        ) {
          data.audioUrl = await copyFile(data.audioUrl);
        }
        await saveFlyer(newFlyerId, uid, data);
        loadFlyers();
      } catch (error) {
        alert(`Could not duplicate the project because of ${error.message}`);
        reportBFError(
          new Error(
            'Could duplicate save the project because of ' + error.message
          )
        );
        setViewState(ViewState.Ready);
      }
    },
    [loadFlyers, copyFile, viewState]
  );

  const deleteFlyer = React.useCallback(
    async (flyer: FlyerData) => {
      try {
        if (viewState !== ViewState.Ready) {
          return;
        }
        const result = confirm(
          `Are you sure you want to delete the flyer "${flyer.name}"?`
        );
        if (!result) {
          return;
        }
        var filesToRemove = [flyer.thumbnailUrl!];
        // audio
        if (
          flyer.audioUrl &&
          isUrlOnAWS(flyer.audioUrl) &&
          !isTemplateAsset(flyer.audioUrl)
        ) {
          filesToRemove.push(flyer.audioUrl);
        }
        // frames
        if (flyer.layers) {
          flyer.layers.map(layer => {
            layer.frames.map((frame: any) => {
              if (
                frame.url &&
                isUrlOnAWS(frame.url) &&
                !isTemplateAsset(frame.url)
              ) {
                filesToRemove.push(frame.url);
              }
            });
          });
        }
        setViewState(ViewState.Deleting);
        // delete db record
        await deleteFlyerNode(flyer.id!);
        console.log('Remove:...');
        console.log(filesToRemove);
        // delete files
        while (filesToRemove.length) {
          const url = filesToRemove.shift();
          if (url) {
            await deleteFileWithUrl(url);
          }
        }
        const fi = flyers.indexOf(flyer);
        const newFlyers = [...flyers];
        newFlyers.splice(fi, 1);
        setFlyers(newFlyers);
        setViewState(ViewState.Ready);
        setTimeout(() => {
          if (flyer.id === getStoreState().flyer.id) {
            // getStoreState().app.history.replace('/');
            storeDispatch(Actions.createNew());
          }
        }, 100);
      } catch (error) {
        alert(`Could not delete the flyer because of ${error.message}`);
        reportBFError(
          new Error('Could not delete the flyer because of ' + error.message)
        );
        setViewState(ViewState.Ready);
      }
    },
    [viewState, flyers]
  );

  if (viewState === ViewState.Loading) {
    return <Spinner />;
  }

  const processing =
    viewState === ViewState.Copying || viewState === ViewState.Deleting;

  const trimmedQuery = query.trim().toLowerCase();
  const filteredProjects = flyers.filter(f => {
    return (
      trimmedQuery.length === 0 ||
      (f.name !== undefined && f.name.toLowerCase().indexOf(trimmedQuery) >= 0)
    );
  });

  if (flyers.length === 0) {
    return <EmptyState>You haven’t saved any project yet.</EmptyState>;
  }

  return (
    <div className={generateClassName('flyer-list', { deleting: processing })}>
      {flyers.length > 20 && (
        <input
          type="search"
          className="filter"
          ref={searchTextField}
          placeholder="Filter projects"
          value={query}
          onChange={e => setQuery(e.currentTarget.value)}
        />
      )}
      {filteredProjects.map((flyer, key) => {
        return (
          <ItemView
            data={flyer}
            key={flyer.id}
            onSelect={didSelectFlyer}
            onRename={renameFlyer}
            onDuplicate={duplicateFlyer}
            onDelete={deleteFlyer}
          />
        );
      })}
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/library/Projects.tsx